import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppConfig } from '@/hooks/useShared';
import { Card, LoadingSpinner, Banner, Button } from '@/components/ui';
import { useCollectionData } from '@/hooks/useCollectionData';
import { GenericDataTable, ColumnConfig } from '@/components/explorer/GenericDataTable';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { doc, deleteDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const formatTimestamp = (ts: any) => ts?.toDate ? format(ts.toDate(), 'MMM d, yyyy HH:mm') : '-';

const formatExplorerCurrency = (value: unknown, symbol: string) => {
  const amount = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(amount) ? formatCurrency(amount, symbol) : '-';
};

const AdminPill = ({ isAdmin }: { isAdmin: boolean }) => {
  if (!isAdmin) return <span className="text-[#6b7280]/30">—</span>;
  return (
    <span className="px-2.5 py-1 bg-[#dcfce7] text-[#166534] text-[10px] font-bold rounded-full uppercase tracking-wide">
      Admin
    </span>
  );
};

export function DataExplorerPage() {
  const { user } = useAuth();
  const { config } = useAppConfig();
  const currencySymbol = config?.currency_symbol || '$';
  const [collectionName, setCollectionName] = useState('games');
  const [limit, setLimit] = useState(50);

  const COLLECTIONS: Record<string, { orderBy: string, columns: ColumnConfig[] }> = {
    games: {
      orderBy: 'created_at',
      columns: [
        { key: 'date', label: 'Date' },
        { key: 'status', label: 'Status', isStatus: true },
        { key: 'total_pot', label: 'Total Pot', format: (val) => formatExplorerCurrency(val, currencySymbol), align: 'right', isMonospace: true },
        { key: 'created_at', label: 'Created At', format: formatTimestamp },
      ]
    },
    users: {
      orderBy: 'created_at',
      columns: [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'is_admin', label: 'Admin', format: (val: boolean) => <AdminPill isAdmin={val} />, align: 'center' },
        { key: 'created_at', label: 'Joined', format: formatTimestamp },
      ]
    },
    settlement_transactions: {
      orderBy: 'created_at',
      columns: [
        { key: 'from_user_id', label: 'From User', isMonospace: true },
        { key: 'to_user_id', label: 'To User', isMonospace: true },
        { key: 'amount', label: 'Amount', format: (val) => formatExplorerCurrency(val, currencySymbol), align: 'right', isMonospace: true },
        { key: 'status', label: 'Status', isStatus: true },
      ]
    },
    settlement_attempts: {
      orderBy: 'timestamp',
      columns: [
        { key: 'timestamp', label: 'Timestamp', format: formatTimestamp },
        { key: 'status', label: 'Status', isStatus: true },
        { key: 'attempted_by', label: 'By', isMonospace: true },
        { key: 'transfer_count', label: 'Transfers', align: 'right', isMonospace: true },
      ]
    },
    admin_audit_log: {
      orderBy: 'timestamp',
      columns: [
        { key: 'action', label: 'Action' },
        { key: 'user_id', label: 'User ID', isMonospace: true },
        { key: 'game_id', label: 'Game ID', isMonospace: true },
        { key: 'timestamp', label: 'Timestamp', format: formatTimestamp },
      ]
    },
    app_config: {
      orderBy: 'app_version',
      columns: [
        { key: 'id', label: 'ID', isMonospace: true },
        { key: 'app_version', label: 'Version', align: 'center' },
        { key: 'default_buy_in', label: 'Buy In', format: (val) => formatExplorerCurrency(val, currencySymbol), align: 'right', isMonospace: true },
        { key: 'default_game_title', label: 'Title' },
      ]
    }
  };

  const currentConfig = COLLECTIONS[collectionName];
  const { data, loading, error, refresh } = useCollectionData(collectionName, limit, currentConfig.orderBy);

  const handleDelete = async (docId: string) => {
    try {
      // 1. Log the deletion to admin_audit_log
      await addDoc(collection(db, 'admin_audit_log'), {
        action: `deleted record ${docId} from ${collectionName}`,
        user_id: user?.id || 'unknown',
        timestamp: serverTimestamp(),
      });

      // 2. Perform the actual deletion
      await deleteDoc(doc(db, collectionName, docId));
      await refresh();
    } catch (err) {
      console.error("Error deleting document:", err);
      throw err;
    }
  };

  const canDelete = collectionName !== 'admin_audit_log';

  return (
    <div className="min-h-screen bg-[#f5f7f8] py-6 px-4 space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
            <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">ADMIN TOOLS</div>
            <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Data Explorer</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refresh()}
            className="bg-white hover:bg-gray-50 border border-gray-200 transition-all"
          >
            <span className="material-symbols-outlined text-[18px]">refresh</span>
            Refresh Database
          </Button>
        </div>

        <Card className="p-0 overflow-hidden border border-gray-200 rounded-[1rem]">
          {/* Controls Section */}
          <div className="p-6 bg-white border-b border-gray-100">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 space-y-1.5">
                <label className="text-[10px] font-black text-[#6b7280] uppercase tracking-[2px] block">Source Collection</label>
                <div className="relative group">
                  <select
                    value={collectionName}
                    onChange={e => setCollectionName(e.target.value)}
                    className="w-full bg-[#f5f7f8] border border-gray-200 hover:border-[#006855]/20 rounded-xl px-4 py-3 text-[#2c2f30] font-semibold text-sm outline-none appearance-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855] transition-all cursor-pointer"
                  >
                    {Object.keys(COLLECTIONS).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6b7280] group-hover:text-[#006855] transition-colors">
                    <span className="material-symbols-outlined text-xl">expand_more</span>
                  </div>
                </div>
              </div>
              <div className="sm:w-48 space-y-1.5">
                <label className="text-[10px] font-black text-[#6b7280] uppercase tracking-[2px] block">Fetch Limit</label>
                <div className="relative group">
                  <select
                    value={limit}
                    onChange={e => setLimit(Number(e.target.value))}
                    className="w-full bg-[#f5f7f8] border border-gray-200 hover:border-[#006855]/20 rounded-xl px-4 py-3 text-[#2c2f30] font-semibold text-sm outline-none appearance-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855] transition-all cursor-pointer"
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={0}>All Records</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6b7280] group-hover:text-[#006855] transition-colors">
                    <span className="material-symbols-outlined text-xl">expand_more</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Section */}
          <div className="bg-white">
            {error && (
              <div className="p-6">
                <Banner type="error">{error}</Banner>
              </div>
            )}

            {loading ? (
              <div className="py-24">
                <LoadingSpinner message={`Syncing ${collectionName}...`} />
              </div>
            ) : (
              <div className="animate-in fade-in slide-in-from-bottom-2 duration-700">
                <GenericDataTable
                  data={data}
                  columns={currentConfig.columns}
                  onDelete={canDelete ? handleDelete : undefined}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
